/**
 * Per-stage resolution list for forward lifecycle jumps.
 *
 * When a move passes over intermediate stages, none of them may be completed
 * silently: the mover must resolve each one — either record the date it
 * actually happened, or skip it with a required reason. This component renders
 * that checklist; the server independently enforces the same rule
 * (`assertStageResolutionsCoverJump`), so the UI is a collector, not the gate.
 *
 * Shared by the Move-stage modal (MoveLifecycleModal) and the kanban board's
 * move-confirm modal (MovePhaseConfirmModal).
 */

import React from 'react';
import {CalendarCheck, SkipForward} from 'lucide-react';
import type {AccountsSubStage} from '@api/generated/client';
import {cn} from '@common-web/lib/utils';
import {Input} from '@common-web/components/ui/input';

/** One passed-over stage's resolution, as the tRPC mutations expect it. */
export type StageResolution =
  | {subStage: AccountsSubStage; action: 'dated'; date: Date}
  | {subStage: AccountsSubStage; action: 'skipped'; reason: string};

/** In-progress form state for one stage (nothing chosen yet ⇒ action null). */
export type StageResolutionDraft = {
  action: 'dated' | 'skipped' | null;
  /** yyyy-MM-dd (native date input). */
  date: string;
  reason: string;
};

export const EMPTY_RESOLUTION_DRAFT: StageResolutionDraft = {action: null, date: '', reason: ''};

/** Today as a native date-input value (local time). */
export function todayDateInputValue(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Parse a date-input value at local noon so the picked day never shifts TZ. */
export function dateInputToDate(value: string): Date {
  return new Date(`${value}T12:00:00`);
}

/**
 * Convert drafts to the mutation payload. Returns null while any step is
 * unresolved (no action, dated without a date, or skipped without a reason) —
 * the caller disables Confirm on null.
 */
export function buildStageResolutions(
  steps: Array<{subStage: AccountsSubStage}>,
  drafts: Record<string, StageResolutionDraft>,
): StageResolution[] | null {
  const out: StageResolution[] = [];
  for (const step of steps) {
    const draft = drafts[step.subStage] ?? EMPTY_RESOLUTION_DRAFT;
    if (draft.action === 'dated' && draft.date) {
      out.push({subStage: step.subStage, action: 'dated', date: dateInputToDate(draft.date)});
    } else if (draft.action === 'skipped' && draft.reason.trim().length > 0) {
      out.push({subStage: step.subStage, action: 'skipped', reason: draft.reason.trim()});
    } else {
      return null;
    }
  }
  return out;
}

interface StageResolutionFieldsProps {
  steps: Array<{subStage: AccountsSubStage; label: string}>;
  drafts: Record<string, StageResolutionDraft>;
  onDraftChange: (subStage: AccountsSubStage, draft: StageResolutionDraft) => void;
  disabled?: boolean;
}

export function StageResolutionFields({
  steps,
  drafts,
  onDraftChange,
  disabled = false,
}: StageResolutionFieldsProps): React.ReactElement {
  return (
    <div className='space-y-2'>
      <p className='text-xs text-muted-foreground'>
        This move passes over the steps below. For each one, record when it actually happened — or
        skip it with a reason. Nothing is marked complete automatically.
      </p>
      {steps.map((step) => {
        const draft = drafts[step.subStage] ?? EMPTY_RESOLUTION_DRAFT;
        return (
          <div
            key={step.subStage}
            className='rounded-lg border border-border px-3 py-2'
            data-testid={`stage-resolution-${step.subStage}`}
          >
            <div className='flex items-center justify-between gap-2'>
              <span className='min-w-0 truncate text-sm font-medium text-foreground'>
                {step.label}
              </span>
              <div className='flex shrink-0 gap-1'>
                <button
                  type='button'
                  disabled={disabled}
                  onClick={() =>
                    onDraftChange(step.subStage, {
                      ...draft,
                      action: 'dated',
                      date: draft.date || todayDateInputValue(),
                    })
                  }
                  className={cn(
                    'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors',
                    draft.action === 'dated'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:border-primary hover:text-foreground',
                  )}
                >
                  <CalendarCheck className='h-3 w-3' />
                  Happened on
                </button>
                <button
                  type='button'
                  disabled={disabled}
                  onClick={() => onDraftChange(step.subStage, {...draft, action: 'skipped'})}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors',
                    draft.action === 'skipped'
                      ? 'border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-400'
                      : 'border-border text-muted-foreground hover:border-amber-500 hover:text-foreground',
                  )}
                >
                  <SkipForward className='h-3 w-3' />
                  Skip
                </button>
              </div>
            </div>
            {draft.action === 'dated' ? (
              <Input
                type='date'
                value={draft.date}
                max={todayDateInputValue()}
                disabled={disabled}
                onChange={(e) => onDraftChange(step.subStage, {...draft, date: e.target.value})}
                className='mt-2 h-8 w-44 text-xs'
              />
            ) : null}
            {draft.action === 'skipped' ? (
              <Input
                type='text'
                value={draft.reason}
                placeholder='Why is this step being skipped? (required)'
                disabled={disabled}
                onChange={(e) => onDraftChange(step.subStage, {...draft, reason: e.target.value})}
                className='mt-2 h-8 text-xs'
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
